import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 relative">
      <div className="absolute inset-0 mandala-bg pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6" data-testid="reset-back-link"><ArrowLeft className="w-4 h-4 mr-1"/>Back to sign in</Link>
        {!token ? (
          <div className="card-soft p-8 text-center" data-testid="reset-invalid">
            <h1 className="font-heading text-3xl text-primary">Invalid link</h1>
            <p className="text-muted-foreground mt-3">This reset link is missing or invalid. Please request a new one.</p>
            <Button asChild className="mt-6 rounded-full"><Link to="/forgot-password">Request new link</Link></Button>
          </div>
        ) : done ? (
          <div className="card-soft p-8 text-center" data-testid="reset-success">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-3xl text-primary">Password updated</h1>
            <p className="text-muted-foreground mt-3">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl text-primary">Set a new password</h1>
            <p className="text-muted-foreground mt-2">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="rp-pass">New password</Label>
                <Input id="rp-pass" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input id="rp-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-1.5" />
              </div>
              {err && <p className="text-sm text-destructive" data-testid="reset-error">{err}</p>}
              <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-press" data-testid="reset-submit-btn">
                {loading ? "Updating..." : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
